import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Heading1 } from './Heading1';
import { Heading2 } from './Heading2';
import { Heading3 } from './Heading3';
import { Paragraph } from './Paragraph';
import { Quote } from './Quote';
import { CodeBlock } from './CodeBlock';
import { Divider } from './Divider';
import Image from '@/components/common/Image';
import { ImageLightbox } from './ImageLightbox';
import { motion } from 'motion/react';

interface MarkdownContentProps {
  content: string;
}

export const MarkdownContent: React.FC<MarkdownContentProps> = ({
  content,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="prose prose-lg dark:prose-invert max-w-none mb-8"
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ node, children, ...props }) => (
            <Heading1 {...props}>{children}</Heading1>
          ),
          h2: ({ node, children, ...props }) => (
            <Heading2 {...props}>{children}</Heading2>
          ),
          h3: ({ node, children, ...props }) => (
            <Heading3 {...props}>{children}</Heading3>
          ),
          p: ({ node, children, ...props }) => (
            <Paragraph {...props}>{children}</Paragraph>
          ),
          blockquote: ({ node, children, ...props }) => (
            <Quote {...props}>{children}</Quote>
          ),
          ul: ({ node, ...props }) => (
            <ul className="list-disc pl-6 mb-6 space-y-2" {...props} />
          ),
          ol: ({ node, ...props }) => (
            <ol className="list-decimal pl-6 mb-6 space-y-2" {...props} />
          ),
          li: ({ node, ...props }) => (
            <li className="text-lg dark:text-zinc-300" {...props} />
          ),
          code: ({ node, className, children = '', ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            return match ? (
              <CodeBlock
                code={String(children).replace(/\n$/, '')}
                language={match[1]}
              />
            ) : (
              <code
                className="bg-zinc-100 dark:bg-zinc-800 px-1.5 py-0.5 rounded text-sm font-mono"
                {...props}
              >
                {children}
              </code>
            );
          },
          a: ({ node, ...props }) => (
            <a
              className="text-blue-600 dark:text-blue-400 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            />
          ),
          hr: () => <Divider />,
          img: ({ src, alt }: { src?: string; alt?: string }) => {
            const [imageError, setImageError] = React.useState(false);
            const [aspectRatio, setAspectRatio] = React.useState(16 / 9);
            const containerRef = React.useRef<HTMLDivElement>(null);
            const [isOpen, setIsOpen] = React.useState(false);

            // Calculate and set container height based on width and aspect ratio
            React.useEffect(() => {
              if (containerRef.current) {
                const updateHeight = () => {
                  const width = containerRef.current?.clientWidth || 0;
                  const height = width / aspectRatio;
                  if (containerRef.current) {
                    containerRef.current.style.height = `${height}px`;
                  }
                };

                // Set initial height
                updateHeight();

                // Update height on window resize
                window.addEventListener('resize', updateHeight);
                return () => window.removeEventListener('resize', updateHeight);
              }
            }, [aspectRatio]);

            // Preload image to get its natural aspect ratio
            React.useEffect(() => {
              if (src && !imageError) {
                const img = new window.Image();
                img.onload = () => {
                  setAspectRatio(img.naturalWidth / img.naturalHeight);
                };
                img.onerror = () => setImageError(true);
                img.src = src;
              }
            }, [src, imageError]);

            return (
              <>
                <div
                  ref={containerRef}
                  className="relative w-full rounded-lg overflow-hidden mb-4 cursor-pointer"
                  onClick={() => setIsOpen(true)}
                >
                  {imageError ? (
                    <div className="w-full h-full flex items-center justify-center bg-zinc-100 dark:bg-zinc-800">
                      <div className="text-center p-4">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-12 w-12 mx-auto text-zinc-400 mb-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <p className="text-zinc-500 dark:text-zinc-400">
                          Image could not be loaded
                        </p>
                      </div>
                    </div>
                  ) : (
                    <Image
                      src={src || ''}
                      alt={alt || ''}
                      fill
                      style={{ objectFit: 'cover' }}
                      className="transition-transform duration-500 hover:scale-105"
                      onError={() => setImageError(true)}
                    />
                  )}
                </div>

                {isOpen && !imageError && src && (
                  <ImageLightbox
                    src={src}
                    alt={alt || ''}
                    onClose={() => setIsOpen(false)}
                  />
                )}
              </>
            );
          },
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto mb-8">
              <table
                className="min-w-full divide-y divide-zinc-200 dark:divide-zinc-700"
                {...props}
              />
            </div>
          ),
          thead: ({ node, ...props }) => (
            <thead className="bg-zinc-50 dark:bg-zinc-800" {...props} />
          ),
          tbody: ({ node, ...props }) => (
            <tbody
              className="bg-white dark:bg-zinc-900 divide-y divide-zinc-200 dark:divide-zinc-800"
              {...props}
            />
          ),
          tr: ({ node, ...props }) => <tr {...props} />,
          th: ({ node, ...props }) => (
            <th
              className="px-6 py-3 text-left text-xs font-medium text-zinc-500 dark:text-zinc-300 uppercase tracking-wider"
              {...props}
            />
          ),
          td: ({ node, ...props }) => (
            <td
              className="px-6 py-4 whitespace-nowrap text-sm text-zinc-800 dark:text-zinc-200"
              {...props}
            />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </motion.div>
  );
};
